<script setup lang="ts">
import {
  ref,
  computed,
  onBeforeMount,
  watch,
} from 'vue';

import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

import { CrossIcon } from '@thehotelsnetwork/icons/vue/linear';
import { useNotifications } from '@thehotelsnetwork/lampo';

import { useChatBot, useSatismeter } from '@thehotelsnetwork/usables';
import { useFeatureFlags } from '@thehotelsnetwork/vue-feature-flags';
import { useUserTracking } from '@thehotelsnetwork/vue-user-tracking';
import { useProductTour } from '@thehotelsnetwork/vue-product-tour';

import CoreApp from '@/components/Core/App.vue';
import auth from '@/modules/Auth';
import browserStorage from '@/modules/BrowserStorage';
import { useAccountsService } from '@/modules/Accounts/services/AccountsService';
import { useUserService } from '@/modules/User/services/UserService';
import { corporateWebLangs } from '@/config';

type NoAccessCopys = {
  title: string,
  content: string,
  moreInfo: string,
}

const route = useRoute();
const { t, locale } = useI18n();
const loading = ref(false);
const authService = useUserService();
const accountsService = useAccountsService();
const {
  identify: identifyUserTracking,
  registerGlobalProperties,
} = useUserTracking();
const { init: initFeatureFlags, flags: featureFlags } = useFeatureFlags();
const { init: initSatismeter } = useSatismeter({
  writeKey: import.meta.env.VUE_APP_SATISMETER_KEY,
  enabled: import.meta.env.PROD,
});
const { showNotification } = useNotifications();
const {
  identifyUser: identifyProductTourUser,
  getUser: getProductTourUser,
} = useProductTour();

const showNoAccessModal = ref(false);
const noAccesModalCopy = ref<NoAccessCopys>();

const nodeEnv = computed(() => import.meta.env.MODE);
const originalUserId = computed(() => auth.state.impersonatedUser?.loggedAsUserId);
const hasSuperPowers = computed(() => !!auth.state.impersonatedUser?.loggedWithPowerLevels);
const loggedAs = computed(() => authService.user?.email);
const internalUserId = ref('');

const showDebugInfo = ref(true);

const getDemandUrlByLang = () => {
  if (corporateWebLangs.includes(locale.value)) {
    return `https://info.thehotelsnetwork.com/${locale.value}/demand`;
  }
  return 'https://info.thehotelsnetwork.com/en/demand';
};

const hideDebugInfo = () => {
  showDebugInfo.value = false;
};

const beforeUnloadListener = async () => {
  const apcUser = await getProductTourUser();
  if (apcUser) {
    browserStorage.setItem('flow_sequence', Number(apcUser.flow_sequence));
  }
};

const resolveAccountId = () => (
  accountsService.account?.id || authService.user?.default?.account_id || ''
);

const resolveAccountRole = () => (
  accountsService.account?.user?.team_member?.role
  || authService.user?.default?.role_name
);

let identifiedAccountId = '';
let intercomRequestId = 0;

const identifyAccountScopedIntegrations = async () => {
  const currentUser = authService.user;
  if (!currentUser) {
    return;
  }

  const impersonated = auth.isImpersonatedUser();
  const accountId = resolveAccountId();
  const accountRole = resolveAccountRole();

  identifyUserTracking({
    id: currentUser.id,
    accountId,
    firstName: currentUser.first_name,
    lastName: currentUser.last_name,
    email: currentUser.email,
    type: currentUser.type as 'internal' | 'team_member',
    role: accountRole as never,
    currency: currentUser.preferences?.currency,
    language: currentUser.preferences?.language,
    impersonated,
  });
  registerGlobalProperties({ source: 'account-settings' });

  if (accountId) {
    intercomRequestId += 1;
    const requestId = intercomRequestId;

    try {
      const intercomCredentials = await authService.getUserIntercomCredentials(accountId);

      if (requestId !== intercomRequestId) {
        return;
      }

      const { initChatBot } = useChatBot({
        user_id: intercomCredentials.user_id,
        user_hash: intercomCredentials.intercom_secure_id,
        impersonated,
      });

      initChatBot();
    } catch (error) {
      if (requestId !== intercomRequestId) {
        return;
      }

      showNotification.error({
        text: t('The chatbot cannot be initialised'),
      });
    }
  }

  identifyProductTourUser({
    id: currentUser.id,
    firstName: currentUser.first_name,
    lastName: currentUser.last_name,
    email: currentUser.email,
    language: currentUser.preferences?.language ?? 'en',
    type: currentUser.type,
    accountId,
    accountRole,
    flow_sequence: Number(browserStorage.getItem('flow_sequence')) || 1,
    demand: !!currentUser.active_products?.demand,
    safe_direct: !!currentUser.active_products?.safe_direct,
    predictive_personalization: !!currentUser.active_products?.predictive_personalization,
    predictive_audience: !!currentUser.active_products?.predictive_audience,
  });

  identifiedAccountId = accountId;
};

watch(
  () => accountsService.account?.id,
  async (accountId, previousAccountId) => {
    if (
      !accountId
      || accountId === previousAccountId
      || accountId === identifiedAccountId
      || !authService.user
    ) {
      return;
    }

    await identifyAccountScopedIntegrations();
  },
);

onBeforeMount(async () => {
  window.addEventListener('beforeunload', beforeUnloadListener, { capture: true });
  if (
    route.meta.requiresAuth
  ) {
    loading.value = true;
    const userId = auth.getUserId();
    const authUser = auth.getUser();

    internalUserId.value = authUser?.id || '';

    if (userId && authUser && authService.user) {
      const currentUser = authService.user;
      // * Set locale user value
      locale.value = currentUser.preferences?.language || 'en';

      const impersonated = auth.isImpersonatedUser();

      const context = {
        id: currentUser.id,
        firstName: currentUser.first_name,
        lastName: currentUser.last_name,
        email: currentUser.email,
        impersonated,
        app: 'account_settings',
      };

      await initFeatureFlags(context);

      await identifyAccountScopedIntegrations();

      initSatismeter({
        id: `${authService.user.id}${impersonated ? '-impersonated' : ''}`,
        email: authService.user.email,
        first_name: authService.user.first_name,
        last_name: authService.user.last_name,
      });

      loading.value = false;
    }
  }

  // * No access modals
  const noAccessApp = String(route.query['no-access']);

  if (noAccessApp) {
    const noAccessCopys = {
      benchdirect: {
        title: t('Benchmark the key metrics of your direct channel'),
        content: t('Do you want to compare your performance to the market and competition? Access our hotel benchmarking analytics platform.'),
        moreInfo: 'https://www.thehotelsnetwork.com/es/benchdirect/',
      },
      demand: {
        title: t('Ready to take your hotel\'s direct booking strategy to new heights?'),
        content: t('Drive qualified traffic to your hotel\'s website and effortlessly boost conversions thanks to our cutting-edge demand platform and fully-managed marketing services. Request a demo.'),
        moreInfo: getDemandUrlByLang(),
      },
      admin: {
        title: t('Personalize your hotel website\'s full funnel'),
        content: t('Do you want to get more direct bookings? Use personalization tools to tailor messages and offers according to each individual\'s behaviour.'),
        moreInfo: 'https://www.thehotelsnetwork.com/es/conversion-personalization/',
      },
      personalization: {
        title: t('Personalize your hotel website\'s full funnel'),
        content: t('Do you want to get more direct bookings? Use personalization tools to tailor messages and offers according to each individual\'s behaviour.'),
        moreInfo: 'https://info.thehotelsnetwork.com/en/reinvented-personalization',
      },
    };

    noAccesModalCopy.value = noAccessCopys[(noAccessApp ?? 'benchdirect') as keyof typeof noAccessCopys];

    if (noAccesModalCopy.value) {
      showNoAccessModal.value = true;
    }
  }
});
</script>

<template>
  <div>
    <Transition name="fade">
      <div
        v-if="loading"
        class="flex fixed inset-0 z-10 justify-center items-center bg-white"
      >
        <LpLoader
          class="text-primary-500"
          ring-size="50"
        />
      </div>
    </Transition>

    <Transition name="fade">
      <CoreApp v-if="!loading" />
    </Transition>

    <teleport
      to="body"
    >
      <div
        v-if="nodeEnv !== 'production'"
        class="flex fixed top-0 right-0 left-0 z-50 justify-center border-t-4 border-danger-500"
      >
        <div class="inline-block px-3 pb-1 text-xs text-white uppercase rounded-b-lg shadow bg-danger-500">
          {{ nodeEnv }}
        </div>
      </div>

      <div
        v-if="nodeEnv !== 'production' && originalUserId && showDebugInfo"
        class="flex fixed bottom-0 left-0 z-50 justify-center w-full opacity-75 transition-opacity hover:opacity-50"
      >
        <!-- eslint-disable vue/no-bare-strings-in-template -->
        <div class="flex gap-4 p-2 text-xs rounded bg-primary-300">
          <div>
            <pre>LoggedAs</pre>
            <div class="flex">
              <pre class="font-extrabold">{{ loggedAs }}</pre>
              <pre
                v-if="hasSuperPowers"
                class="ml-2"
              >💥</pre>
            </div>
          </div>

          <div class="w-px h-8 bg-base-900" />

          <div>
            <pre>Master of puppets</pre>
            <pre class="font-extrabold">{{ internalUserId }}</pre>
          </div>
          <LpButton
            mode="transparent"
            color="gray"
            size="small"
            @click="hideDebugInfo"
          >
            <CrossIcon class="w-5 h-5" />
          </LpButton>
        </div>
        <!-- eslint-enable vue/no-bare-strings-in-template -->
      </div>

      <LpModal
        v-model="showNoAccessModal"
        content-classes="showNoAccessModal"
      >
        <template #content>
          <div class="pr-3 pl-2 mt-3">
            <h2 class="title2">
              {{ noAccesModalCopy?.title }}
            </h2>
            <div class="mt-4 text-xl leading-4">
              {{ noAccesModalCopy?.content }}
            </div>
          </div>
          <div class="flex justify-end pt-6 mt-14 space-x-2 font-bold border-t border-base-400">
            <LpButton
              mode="outline"
              color="gray"
              @click="showNoAccessModal = false"
            >
              {{ t('Not now') }}
            </LpButton>
            <a
              :href="noAccesModalCopy?.moreInfo"
              target="_blank"
              rel="noopener noreferrer"
            >
              <LpButton>
                {{ t('Get more info') }}
              </LpButton>
            </a>
          </div>
        </template>
      </LpModal>
    </teleport>
  </div>
</template>

<style>
/* Needed to prevent Appcues renders over modal */
.lpmodal {
  z-index: 2147483647;
}
.showNoAccessModal {
  width: 37.5rem;
}
</style>
